/**
 * The two per-location Datenschutzerklärung URL families
 * (spec 10.01.02-Navigation-Structure § 2.3.3, 10.02.09-Privacy-Policy-Page):
 *
 * - `/datenschutzerklaerung/{slug}-{centerNumber}/` — GmbH Standorte
 * - `/datenschutzerklaerung-franchise/{slug}-{centerNumber}/` — franchise Standorte
 *
 * The URL segment is **composite**: it is not a lookup key but a Location slug
 * with the SH-issued `centerNumber` appended, so resolving one means parsing it
 * first and then verifying the number against the Location the slug names. That
 * verification is what stops `/datenschutzerklaerung/leipzig-lausen-9999/` from
 * serving Leipzig-Lausen's policy under a wrong number — a legally wrong page,
 * not just duplicate content.
 *
 * Lives in `packages/shared` for the reason `./cityPath` and
 * `./canonicalOrigins` give: three independent places have to agree on this
 * shape byte-for-byte — the website routes, the School Details widget's derived
 * privacy link (10.03.02.01.13 § AC 3) and the backend's cache-purge targets —
 * and a second spelling beside this one is exactly the drift this file prevents.
 */

/** Prefix of the GmbH per-location Datenschutzerklärung family. */
export const PRIVACY_GMBH_PREFIX = '/datenschutzerklaerung/';

/** Prefix of the franchise per-location Datenschutzerklärung family. */
export const PRIVACY_FRANCHISE_PREFIX = '/datenschutzerklaerung-franchise/';

/**
 * Which of the two per-location documents a URL addresses. The string values
 * are what the backend's purge targets and the website's routes pass around.
 */
export enum PrivacyPolicyVariant {
  Gmbh = 'gmbh',
  Franchise = 'franchise',
}

/** The prefix each variant is served under. */
export function privacyPolicyPrefix(variant: PrivacyPolicyVariant): string {
  return variant === PrivacyPolicyVariant.Franchise
    ? PRIVACY_FRANCHISE_PREFIX
    : PRIVACY_GMBH_PREFIX;
}

/**
 * `{location.slug}-{location.centerNumber}` — the composite URL segment both
 * families are addressed by (10.01.02 § 2.3.3).
 */
export function privacyPolicySegment(
  slug: string,
  centerNumber: number,
): string {
  return `${slug}-${centerNumber}`;
}

/**
 * Inverse of {@link privacyPolicySegment}: `undefined` when the segment carries
 * no usable numeric tail.
 *
 * The slug part is matched greedily, so a Standort whose own slug ends in
 * digits still parses correctly (`berlin-1-234` → slug `berlin-1`, number 234).
 *
 * Rejected, all of which would otherwise resolve to a Location that the URL
 * does not actually name:
 * - no `-`-separated numeric tail at all (`leipzig-lausen`);
 * - a digits-only segment (`5306`), which leaves an empty slug;
 * - a leading-zero tail (`leipzig-05306`), because `Number('05306')` is 5306
 *   and the canonical URL for that Location is spelled without the zero — two
 *   URLs for one page;
 * - a tail that is not a safe integer.
 */
export function parsePrivacyPolicySegment(
  segment: string,
): { slug: string; centerNumber: number } | undefined {
  const match = /^(.+)-(\d+)$/.exec(segment);
  if (!match) {
    return undefined;
  }
  const slug = match[1]!;
  const digits = match[2]!;
  if (digits.length > 1 && digits.startsWith('0')) {
    return undefined;
  }
  const centerNumber = Number(digits);
  if (!Number.isSafeInteger(centerNumber)) {
    return undefined;
  }
  return { slug, centerNumber };
}

/**
 * The complete public path of a Location's Datenschutzerklärung, trailing
 * slash included (`trailingSlash: true` is canonical site-wide).
 *
 * The one builder producing a whole privacy URL: the School Details widget
 * derives its link with it and the backend purges with it, so neither can
 * disagree with the route about where the page lives.
 */
export function privacyPolicyPath(
  variant: PrivacyPolicyVariant,
  slug: string,
  centerNumber: number,
): string {
  return `${privacyPolicyPrefix(variant)}${privacyPolicySegment(slug, centerNumber)}/`;
}

/**
 * The Standort's own Datenschutzerklärung path, or `undefined` when the
 * Location lacks the parts the URL is built from. A Standort without a
 * `centerNumber` has no per-location privacy page at all, so callers fall
 * through to the domain-level policy rather than linking a 404.
 *
 * `franchise` decides the family: a franchise Standort is its own data
 * controller and its policy names the Franchise-Partner, so it must not be
 * sent to the GmbH document (10.02.09 § AC 1). Shared by the School Details
 * widget and the `{location.privacyPolicyPath}` placeholder, which is what
 * every editor-authored privacy link resolves through.
 */
export function derivedPrivacyPolicyPath(location: {
  slug?: string | undefined;
  centerNumber?: number | undefined;
  franchise?: boolean | undefined;
}): string | undefined {
  if (!location.slug || location.centerNumber == null) {
    return undefined;
  }
  return privacyPolicyPath(
    location.franchise
      ? PrivacyPolicyVariant.Franchise
      : PrivacyPolicyVariant.Gmbh,
    location.slug,
    location.centerNumber,
  );
}
